import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, DChip, Icon, Modal, PickerChip } from '@/components/kit';
import { externalProvidersApi } from '@/services/api/externalProvidersApi';
import { toast } from 'sonner';
import { useIsBelowLg } from '@/hooks/use-mobile';
import {
  CATEGORY_OPTIONS,
  amount,
  dayLabel,
  uploadExpenseDocument,
  type ExpenseCategory,
  type ExpenseLine,
} from '../lib/useAccounts';

/**
 * The three dialogs the expense screens open: Record an expense (`g_m_rexp`,
 * `g_e2`), Add a recurring cost (`g_m_radd`) and Attach the invoice
 * (`g_m_attach`).
 *
 * THE PHOTO IS THE RECORD, the boards say — so choosing a file uploads it to
 * the clinic's own encrypted store at once, and the expense carries the
 * document's id. A typed reference still counts as paper for a supplier who
 * only sends a number. Undocumented is counted against either.
 */

export interface PickedDoc {
  id?: string;
  name: string;
}

/** Photograph / choose-a-file zone. Uploads the file and hands back its id and name. */
function FileZone({
  kind,
  onPick,
  picked,
}: {
  kind: 'photo' | 'file';
  onPick: (doc: PickedDoc) => void;
  picked?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="card"
      disabled={busy}
      style={{
        margin: 0,
        flex: 1,
        minHeight: 110,
        borderStyle: 'dashed',
        display: 'grid',
        placeItems: 'center',
        textAlign: 'center',
        cursor: 'pointer',
        background: kind === 'photo' ? 'var(--info-100, var(--surface-100))' : undefined,
      }}
      onClick={() => ref.current?.click()}
    >
      <input
        ref={ref}
        type="file"
        hidden
        accept={kind === 'photo' ? 'image/*' : 'image/*,application/pdf'}
        {...(kind === 'photo' ? { capture: 'environment' as const } : {})}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          setBusy(true);
          uploadExpenseDocument(f)
            .then((d) => onPick({ id: d.id, name: d.name }))
            .catch((err: unknown) =>
              toast.error(
                (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
                  'The file was not uploaded.',
              ),
            )
            .finally(() => setBusy(false));
        }}
      />
      <span>
        <Icon name={kind === 'photo' ? 'camera' : 'file-upload'} />
        <p className="t-sec mut mt4">
          {busy ? 'uploading…' : picked ? picked : kind === 'photo' ? 'photograph the invoice' : 'choose a file'}
        </p>
        {kind === 'photo' && !picked && !busy ? <p className="t-cap mut">the photo is the record</p> : null}
      </span>
    </button>
  );
}

export interface RecordInput {
  description: string;
  amountMinor: number;
  category: ExpenseCategory;
  spentOn: string;
  paidWith?: string;
  supplierName?: string;
  documentId?: string;
  documentName?: string;
  vatMinor?: number;
  note?: string;
  dueOn?: string;
  paidOn?: string;
}

const PAID_HOW = ['Bank transfer', 'Cash', 'From the drawer'];

export function RecordModal({
  open,
  period,
  onClose,
  onSave,
}: {
  open: boolean;
  period: string;
  onClose: () => void;
  onSave: (input: RecordInput, opts: { monthly: boolean; another: boolean }) => void;
}) {
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [category, setCategory] = useState<ExpenseCategory>('other');
  const [spentOn, setSpentOn] = useState(`${period}-01`);
  const [paidWith, setPaidWith] = useState('Bank transfer');
  const [supplier, setSupplier] = useState('');
  const [document, setDocument] = useState('');
  const [documentId, setDocumentId] = useState<string | undefined>();
  const [vat, setVat] = useState('');
  const [note, setNote] = useState('');
  const [paid, setPaid] = useState(true);
  const [dueOn, setDueOn] = useState('');
  const [monthly, setMonthly] = useState(false);
  /* g_e2 — on the phone this is one column, in the board's order (photo,
     supplier, amount, invoice no., date, category, paid?, how, repeats, note). */
  const phone = useIsBelowLg();
  const ord = (n: number) => (phone ? { order: n } : undefined);

  const directory = useQuery({
    queryKey: ['accountant', 'directory'],
    staleTime: 5 * 60_000,
    retry: false,
    enabled: open,
    queryFn: () => externalProvidersApi.list(),
  });
  const known = (directory.data ?? []).some(
    (p) => supplier.trim() && p.name.trim().toLowerCase() === supplier.trim().toLowerCase(),
  );

  const minor = Math.round(Number(value) * 100);
  const vatMinor = Math.round(Number(vat) * 100) || 0;
  const ready = description.trim().length > 1 && minor > 0 && !!spentOn && vatMinor <= minor;

  const reset = () => {
    setDescription('');
    setValue('');
    setSupplier('');
    setDocument('');
    setDocumentId(undefined);
    setVat('');
    setNote('');
    setDueOn('');
    setMonthly(false);
    setPaid(true);
  };
  const save = (another: boolean) =>
    onSave(
      {
        description: description.trim(),
        amountMinor: minor,
        category,
        spentOn,
        ...(paid && paidWith ? { paidWith: paidWith.toLowerCase() } : {}),
        ...(supplier.trim() ? { supplierName: supplier.trim() } : {}),
        ...(documentId ? { documentId } : {}),
        ...(document.trim() ? { documentName: document.trim() } : {}),
        ...(vatMinor > 0 ? { vatMinor } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(paid ? { paidOn: spentOn } : dueOn ? { dueOn } : {}),
      },
      { monthly, another },
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record an expense"
      sub="money that has already left the clinic — or is owed, and will be paid"
      width={860}
      phoneScreen
      foot={
        <div style={{ display: 'contents' }}>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button
            kind="outline"
            disabled={!ready}
            onClick={() => {
              save(true);
              reset();
            }}
          >
            {phone ? 'Save & another' : 'Save & add another'}
          </Button>
          <Button
            kind="primary"
            disabled={!ready}
            onClick={() => {
              save(false);
              reset();
            }}
          >
            {phone ? 'Save' : 'Save — posts to Expenses and Payables'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(0,1.1fr)', gap: phone ? 0 : 16, alignItems: 'start' }}>
        <div style={phone ? { display: 'contents' } : undefined}>
          <div className="flex" style={{ gap: 8, ...ord(0) }}>
            <FileZone
              kind="photo"
              picked={documentId ? document : undefined}
              onPick={(d) => {
                setDocument(d.name);
                setDocumentId(d.id);
              }}
            />
            <FileZone
              kind="file"
              picked={documentId ? document : undefined}
              onPick={(d) => {
                setDocument(d.name);
                setDocumentId(d.id);
              }}
            />
          </div>
          <Card className="mt12" style={ord(3)}>
            <label className="t-ovl mut" htmlFor="ex-doc">The invoice no. — or its reference</label>
            <input
              id="ex-doc"
              className="fld t-strong mt4"
              placeholder="leave it, and attach the invoice when it arrives"
              value={document}
              onChange={(e) => {
                setDocument(e.target.value);
                setDocumentId(undefined);
              }}
            />
            <p className="t-cap mut mt8">
              Posted is not proven. An expense with no paper still belongs in the books — it counts
              towards Undocumented until the invoice turns up.
            </p>
          </Card>
          <Card className="mt12" kind="warn" style={ord(8)}>
            <label className="t-ovl mut" htmlFor="ex-note">Note (optional)</label>
            <input
              id="ex-note"
              className="fld t-sec mt4"
              placeholder="autoclave spare part — approved by Dr. Mostafa"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Card>
        </div>

        <div style={phone ? { display: 'contents' } : undefined}>
          <Card className={phone ? 'mt12' : undefined} style={ord(1)}>
            <label className="t-ovl mut" htmlFor="ex-who">Supplier</label>
            <input
              id="ex-who"
              className="fld t-strong mt4"
              list="ex-suppliers"
              placeholder="who was paid"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            />
            <datalist id="ex-suppliers">
              {(directory.data ?? []).map((p) => <option key={p.id} value={p.name} />)}
            </datalist>
            {known ? <p className="t-cap brand mt4">from the Directory</p> : null}
          </Card>

          <Card className="mt8" style={ord(2)}>
            <div className="flex" style={{ gap: 12, flexWrap: phone ? 'wrap' : undefined }}>
              <div style={{ flex: 1, minWidth: phone ? '100%' : undefined }}>
                <label className="t-ovl mut" htmlFor="ex-what">What it was for</label>
                <input
                  id="ex-what"
                  className="fld t-strong mt4"
                  autoFocus
                  placeholder="in the clinic's own words"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div style={{ flex: 1, minWidth: phone ? '100%' : undefined }}>
                <label className="t-ovl mut" htmlFor="ex-amt">Amount · VAT</label>
                <div className="flex mt4" style={{ gap: 8 }}>
                  <input
                    id="ex-amt"
                    className="fld t-strong num"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                  <input
                    id="ex-vat"
                    className="fld t-sec num"
                    inputMode="decimal"
                    aria-label="VAT inside the amount"
                    placeholder="VAT"
                    value={vat}
                    onChange={(e) => setVat(e.target.value)}
                  />
                </div>
                {vatMinor > 0 ? (
                  <p className={`t-cap mt4 ${vatMinor > minor ? 'red' : 'mut'}`}>
                    {vatMinor > minor ? 'VAT cannot be more than the amount' : `VAT ${amount(vatMinor)} reclaimable`}
                  </p>
                ) : null}
              </div>
            </div>
          </Card>

          <div className="flex mt8" style={{ gap: 8, flexDirection: phone ? 'column' : undefined, alignItems: phone ? 'stretch' : undefined, ...ord(4) }}>
            <Card style={{ margin: 0, flex: 1 }}>
              <label className="t-ovl mut" htmlFor="ex-when">Date</label>
              <input id="ex-when" className="fld t-strong mt4" type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} />
            </Card>
            <Card style={{ margin: 0, flex: 1 }}>
              <p className="t-ovl mut">Category</p>
              <p className="mt4">
                <PickerChip
                  label={`${CATEGORY_OPTIONS.find((c) => c.value === category)?.label} ▾`}
                  ariaLabel="Category"
                  kind="brand"
                  value={category}
                  onChange={setCategory}
                  options={CATEGORY_OPTIONS}
                />
              </p>
            </Card>
          </div>

          <Card className="mt8" style={ord(5)}>
            <p className="t-ovl mut">Paid?</p>
            <div className="flex mt4" style={{ gap: 8, flexWrap: 'wrap' }}>
              <DChip kind={paid ? 'brand' : 'outline'} onClick={() => setPaid(true)}>Paid</DChip>
              <DChip kind={!paid ? 'brand' : 'outline'} onClick={() => setPaid(false)}>Not yet — it is payable</DChip>
              {!paid ? (
                <input className="fld t-sec" type="date" aria-label="Due on" value={dueOn} onChange={(e) => setDueOn(e.target.value)} style={{ maxWidth: 160 }} />
              ) : null}
            </div>
          </Card>

          {paid ? (
            <Card className="mt8" style={ord(6)}>
              <p className="t-ovl mut">Paid how — when it is paid</p>
              <div className="flex mt4" style={{ gap: 8, flexWrap: 'wrap' }}>
                {PAID_HOW.map((h) => (
                  <DChip key={h} kind={paidWith === h ? 'brand' : 'outline'} onClick={() => setPaidWith(h)}>{h}</DChip>
                ))}
              </div>
            </Card>
          ) : null}

          <Card className="mt8" style={ord(7)}>
            <p className="t-ovl mut">Repeats?</p>
            <div className="flex mt4" style={{ gap: 8, flexWrap: 'wrap' }}>
              <DChip kind={!monthly ? 'brand' : 'outline'} onClick={() => setMonthly(false)}>One-time</DChip>
              <DChip kind={monthly ? 'brand' : 'outline'} onClick={() => setMonthly(true)}>Monthly — make it recurring</DChip>
            </div>
          </Card>
        </div>
      </div>
    </Modal>
  );
}

/** Add a recurring cost — `g_m_radd`. */
export function AddRecurringModal({
  open,
  onClose,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (input: {
    label: string;
    category: ExpenseCategory;
    amountMinor?: number;
    dayOfMonth: number;
    paidWith?: string;
    supplierName?: string;
  }) => void;
}) {
  const [label, setLabel] = useState('');
  const [fixed, setFixed] = useState(true);
  const [value, setValue] = useState('');
  const [day, setDay] = useState('1');
  const [category, setCategory] = useState<ExpenseCategory>('rent');
  const [supplier, setSupplier] = useState('');
  const [paidWith, setPaidWith] = useState('bank transfer');

  const directory = useQuery({
    queryKey: ['accountant', 'directory'],
    staleTime: 5 * 60_000,
    retry: false,
    enabled: open,
    queryFn: () => externalProvidersApi.list(),
  });
  const known = (directory.data ?? []).some(
    (p) => supplier.trim() && p.name.trim().toLowerCase() === supplier.trim().toLowerCase(),
  );

  const minor = Math.round(Number(value) * 100);
  const ready = label.trim().length > 1 && (!fixed || minor > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a recurring cost"
      sub="the ones that repeat forever — typed once, not twelve times"
      width={760}
      phoneScreen
      foot={
        <>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button
            kind="primary"
            disabled={!ready}
            onClick={() => {
              onSave({
                label: label.trim(),
                category,
                ...(fixed ? { amountMinor: minor } : {}),
                dayOfMonth: Math.min(31, Math.max(1, Number(day) || 1)),
                ...(paidWith ? { paidWith } : {}),
                ...(supplier.trim() ? { supplierName: supplier.trim() } : {}),
              });
              setLabel('');
              setValue('');
              setSupplier('');
            }}
          >
            Add the line
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
        <Card style={{ margin: 0 }}>
          <label className="t-ovl mut" htmlFor="rc-label">Line</label>
          <input id="rc-label" className="fld t-strong mt4" autoFocus placeholder="Rent — Maadi" value={label} onChange={(e) => setLabel(e.target.value)} />
        </Card>
        <Card style={{ margin: 0 }}>
          <p className="t-ovl mut">Category</p>
          <p className="mt4">
            <PickerChip
              label={`${CATEGORY_OPTIONS.find((c) => c.value === category)?.label} ▾`}
              ariaLabel="Category"
              kind="brand"
              value={category}
              onChange={setCategory}
              options={CATEGORY_OPTIONS}
            />
          </p>
        </Card>
        <Card style={{ margin: 0 }}>
          <label className="t-ovl mut" htmlFor="rc-amt">Amount</label>
          <input
            id="rc-amt"
            className="fld t-strong mt4 num"
            inputMode="decimal"
            placeholder={fixed ? '0.00' : 'varies'}
            disabled={!fixed}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Card>
        <Card style={{ margin: 0 }}>
          <label className="t-ovl mut" htmlFor="rc-who">Paid to</label>
          <input
            id="rc-who"
            className="fld t-strong mt4"
            list="rc-suppliers"
            placeholder="optional"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
          />
          <datalist id="rc-suppliers">
            {(directory.data ?? []).map((p) => <option key={p.id} value={p.name} />)}
          </datalist>
          {known ? <p className="t-cap brand mt4">from the Directory</p> : null}
        </Card>
        <Card style={{ margin: 0 }}>
          <label className="t-ovl mut" htmlFor="rc-day">Posts on</label>
          <input id="rc-day" className="fld t-strong mt4 num" type="number" min={1} max={31} value={day} onChange={(e) => setDay(e.target.value)} />
          <p className="t-cap mut mt4">the day of the month</p>
        </Card>
        <Card style={{ margin: 0 }}>
          <p className="t-ovl mut">Kind</p>
          <div className="flex mt4" style={{ gap: 8, flexWrap: 'wrap' }}>
            <DChip kind={fixed ? 'brand' : 'outline'} onClick={() => setFixed(true)}>Fixed — posts itself</DChip>
            <DChip kind={!fixed ? 'brand' : 'outline'} onClick={() => setFixed(false)}>Variable — remind me only</DChip>
          </div>
          <label className="t-ovl mut mt12" htmlFor="rc-how">Paid with</label>
          <input id="rc-how" className="fld t-sec mt4" value={paidWith} onChange={(e) => setPaidWith(e.target.value)} />
        </Card>
      </div>
      <Card kind="accent" className="mt12">
        <p className="t-sec brand">
          A fixed line posts itself on its date and stays amber until its invoice is attached. A
          variable line only reminds you — MAIE never posts a number it had to guess.
        </p>
      </Card>
    </Modal>
  );
}

/** Attach the invoice — `g_m_attach`. Posted is not proven. */
export function AttachModal({
  line,
  onClose,
  onSave,
}: {
  line: Pick<ExpenseLine, 'description' | 'amountMinor' | 'spentOn'> | null;
  onClose: () => void;
  onSave: (doc: PickedDoc) => void;
}) {
  const [ref, setRef] = useState('');
  const [docId, setDocId] = useState<string | undefined>();
  const close = () => {
    setRef('');
    setDocId(undefined);
    onClose();
  };
  const pick = (d: PickedDoc) => {
    setRef(d.name);
    setDocId(d.id);
  };
  return (
    <Modal
      open={!!line}
      onClose={close}
      title="Attach the invoice"
      sub="the photo is the record"
      width={680}
      foot={
        <>
          <Button kind="outline" onClick={close}>Cancel</Button>
          <Button
            kind="primary"
            disabled={!docId}
            onClick={() => {
              onSave({ id: docId, name: ref.trim() });
              setRef('');
              setDocId(undefined);
            }}
          >
            Attach the page
          </Button>
        </>
      }
    >
      <div className="flex" style={{ gap: 8 }}>
        <FileZone kind="photo" picked={docId ? ref : undefined} onPick={pick} />
        <FileZone kind="file" picked={docId ? ref : undefined} onPick={pick} />
      </div>
      <Card className="mt12">
        <p className="t-ovl mut">Attaching to</p>
        <p className="t-sec sec mt4">
          {line ? `${line.description} · ${amount(line.amountMinor)}${line.spentOn ? ` · posted ${dayLabel(line.spentOn)}` : ''}` : ''}
        </p>
      </Card>
      <Card kind="warn" className="mt12">
        <p className="t-sec amb">
          Posted is not proven: the line stays amber until this page is attached, so the month
          closes on evidence.
        </p>
      </Card>
    </Modal>
  );
}
